import {
  DependencyMapReferenceParent,
  DependencyMapNode,
  DependencyMapReference,
} from '../types';

const countReferenceParents = (parent: DependencyMapReferenceParent): number =>
  1 + (parent.parent ? countReferenceParents(parent.parent) : 0);

const countAllNodesReducer = (
  itemCount: number,
  { children, incomingReferences, outgoingReferences }: DependencyMapNode
): number =>
  itemCount +
  1 +
  (children?.reduce(countAllNodesReducer, 0) ?? 0) +
  [incomingReferences, outgoingReferences]
    .flatMap(refs => refs ?? [])
    .map(
      ({ node, parent }) =>
        countAllNodesReducer(0, node) +
        (parent ? countReferenceParents(parent) : 0)
    )
    .reduce((count, current) => count + current, 0);
type LimitReferencesReducerState = {
  references: DependencyMapReference[];
  count: number;
  limit: number;
};
const limitReferencesReducer = (
  state: LimitReferencesReducerState,
  reference: DependencyMapReference
) => {
  if (state.count >= state.limit) {
    return state;
  }
  const newReference: DependencyMapReference = {
    ...reference,
    parent: null,
    node: {
      ...reference.node,
      children: null,
      incomingReferences: null,
      outgoingReferences: null,
    },
  };
  state.references.push(newReference);
  state.count++;

  let newParent: DependencyMapReferenceParent | null = null;
  if (reference.parent) {
    let currentParent: DependencyMapReferenceParent | null = reference.parent;
    while (state.count < state.limit && currentParent) {
      newParent = { ...currentParent, parent: null };
      state.count++;
      currentParent = currentParent.parent;
    }
  }
  newReference.parent = newParent;

  if (state.count < state.limit && reference.node.incomingReferences) {
    const { references: newIncomingReferences, count } =
      reference.node.incomingReferences.reduce(limitReferencesReducer, {
        ...state,
        references: [],
      });
    newReference.node.incomingReferences = newIncomingReferences;
    state.count = count;
  }
  if (state.count < state.limit && reference.node.outgoingReferences) {
    const { references: newOutgoingReferences, count } =
      reference.node.outgoingReferences.reduce(limitReferencesReducer, {
        ...state,
        references: [],
      });
    newReference.node.outgoingReferences = newOutgoingReferences;
    state.count = count;
  }
  return state;
};
type LimitNodesReducerState = {
  nodes: DependencyMapNode[];
  count: number;
  limit: number;
};
const limitNodesReducer = (
  state: LimitNodesReducerState,
  node: DependencyMapNode
) => {
  if (state.count >= state.limit) {
    return state;
  }
  const newNode: DependencyMapNode = {
    ...node,
    incomingReferences: null,
    outgoingReferences: null,
    children: null,
  };
  state.nodes.push(newNode);
  state.count++;

  if (state.count < state.limit && node.children) {
    const { count, nodes: newChildren } = node.children.reduce(
      limitNodesReducer,
      { ...state, nodes: [] }
    );
    newNode.children = newChildren;
    state.count = count;
  }
  if (state.count < state.limit && node.incomingReferences) {
    const { references: newIncomingReferences, count } =
      node.incomingReferences.reduce(limitReferencesReducer, {
        ...state,
        references: [],
      });
    newNode.incomingReferences = newIncomingReferences;
    state.count = count;
  }
  if (state.count < state.limit && node.outgoingReferences) {
    const { references: newOutgoingReferences, count } =
      node.outgoingReferences.reduce(limitReferencesReducer, {
        ...state,
        references: [],
      });
    newNode.outgoingReferences = newOutgoingReferences;
    state.count = count;
  }
  return state;
};

const limitNodes = (nodes: DependencyMapNode[], limit: number) => {
  const originalItemCount = nodes.reduce(countAllNodesReducer, 0);
  if (originalItemCount <= limit) {
    return { nodes, itemCount: originalItemCount, originalItemCount };
  }
  return {
    nodes: nodes.reduce(limitNodesReducer, { nodes: [], count: 0, limit })
      .nodes,
    itemCount: limit,
    originalItemCount,
  };
};
export { limitNodes };
